import { Suspense, useEffect } from "react";
import { RouterProvider } from "react-router-dom";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import "./assets/scss/index.scss";
import "./assets/scss/app.css";
import "react-perfect-scrollbar/dist/css/styles.css";
import { Config } from "./Config";
import { SnackbarProvider } from "notistack";
import { Provider } from "react-redux";
import store from "redux/store";
import { withLDProvider } from "launchdarkly-react-client-sdk";
import { Provider as UrqlProvider } from "urql";
import { LDProps } from "launchdarkly-react-client-sdk/lib/withLDConsumer";
import VendorSetup from "VendorSetup";
import theme from "theme";
import useBroadcastChannel from "helpers/hooks/useBroadcastChannel";
import { BroadcastChannelContext } from "contexts/BroadcastChannelContext";
import LoadingSpinner from "components/LoadingSpinner";
import router from "./Router";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import {
  ErrorSnackbar,
  InfoSnackbar,
  LensEditorSendAlertSnackbar,
  FormChageSnackbar,
  RefreshSnackbar,
  ServerConnectionSnackbar,
  SuccessSnackbar,
  MultilineErrorSnackbar,
} from "helpers/Snackbars";
import SessionChanged from "SessionChanged";
import { LD_CONTEXT_GLOBAL } from "LD_CONTEXT";
import { LoaderErrors } from "components/LoaderErrors";
import gqlClient from "./gqlClient";
import { ConfirmationDialogProvider } from "contexts/ConfirmationDialogProvider";

declare module "notistack" {
  interface VariantOverrides {
    refresh: true;
    server: true;
    lensUpdate: {
      callback: () => Promise<any>;
    };
    lensEditorAlert: {
      onCancel: () => Promise<any>;
      onSend: () => Promise<any>;
    };
    multilineError: true;
  }
}

(window as any).zESettings = {
  webWidget: {
    helpCenter: {
      title: {
        "en-US": "Pattern Data Help Center",
      },
    },
    offset: {
      mobile: {
        vertical: "-5px",
      },
    },
  },
};

export const App = (_props: LDProps) => {
  const broadcastChannel = useBroadcastChannel();

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("Initializing Pattern Data, build " + Config.version);
    // eslint-disable-next-line no-console
    console.log(`
  ____       _   _                    ____        _        
 |  _ \\ __ _| |_| |_ ___ _ __ _ __   |  _ \\  __ _| |_ __ _ 
 | |_) / _${"`"} | __| __/ _ \\ '__| '_ \\  | | | |/ _${"`"} | __/ _${"`"} |
 |  __/ (_| | |_| ||  __/ |  | | | | | |_| | (_| | || (_| |
 |_|   \\__,_|\\__|\\__\\___|_|  |_| |_| |____/ \\__,_|\\__\\__,_|
`);
    // const browser = detectBrowser();
    // console.log(browser);
  }, []);

  return (
    <Provider store={store}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <SnackbarProvider
            maxSnack={3}
            Components={{
              info: InfoSnackbar,
              success: SuccessSnackbar,
              error: ErrorSnackbar,
              refresh: RefreshSnackbar,
              server: ServerConnectionSnackbar,
              lensUpdate: FormChageSnackbar,
              lensEditorAlert: LensEditorSendAlertSnackbar,
              multilineError: MultilineErrorSnackbar,
            }}>
            <LoaderErrors />
            <UrqlProvider value={gqlClient}>
              <ConfirmationDialogProvider>
                <BroadcastChannelContext.Provider value={broadcastChannel}>
                  <SessionChanged />
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <VendorSetup>
                      <Suspense
                        fallback={
                          <div className="w-full h-full items-center justify-center">
                            <LoadingSpinner size={8} />
                          </div>
                        }>
                        <RouterProvider router={router} />
                      </Suspense>
                    </VendorSetup>
                  </LocalizationProvider>
                </BroadcastChannelContext.Provider>
              </ConfirmationDialogProvider>
            </UrqlProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </Provider>
  );
};

export const LDApp = withLDProvider({
  clientSideID: Config.launchDarkly,
  context: LD_CONTEXT_GLOBAL.ldContext,
})(App);
